import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { CommunicationsDialogs } from '@/components/communications/CommunicationsDialogs';
import { CommunicationsTabs } from '@/components/communications/CommunicationsTabs';
import { HighVisibilityPageHeader } from '@/components/ui/high-visibility-mobile';
import { MessageSquareText } from 'lucide-react';

const EMAIL_STARTER_HTML = `<div style="font-family:Arial,sans-serif;max-width:640px;margin:0 auto;background:#ffffff;color:#18181b;border:1px solid #e4e4e7;border-radius:10px;overflow:hidden;">
  <div style="background:{{brand_color}};color:#ffffff;padding:24px 28px;">
    <div style="font-size:13px;letter-spacing:.08em;text-transform:uppercase;opacity:.85;">{{business_name}}</div>
    <h1 style="margin:8px 0 0;font-size:26px;line-height:1.2;">Your service update</h1>
  </div>
  <div style="padding:28px;">
    <p style="font-size:16px;line-height:1.6;margin:0 0 16px;">Hi {{customer_name}},</p>
    <p style="font-size:16px;line-height:1.6;margin:0 0 22px;">We have an update about your {{service_category}} request with {{business_name}}.</p>
    <a href="{{quote_link}}" style="display:inline-block;background:{{brand_color}};color:#ffffff;text-decoration:none;padding:12px 18px;border-radius:6px;font-weight:700;">View details</a>
  </div>
  <div style="background:#f4f4f5;padding:16px 28px;font-size:12px;color:#71717a;line-height:1.5;">
    {{business_name}} · {{business_phone}}<br />Reply to this email with any questions.
  </div>
</div>`;

const messageStatusClass = (status) => {
  if (['delivered'].includes(status)) return 'bg-emerald-50 text-emerald-600';
  if (['sent', 'queued', 'sending', 'accepted'].includes(status)) return 'bg-sky-50 text-sky-600';
  if (['failed', 'undelivered'].includes(status)) return 'bg-red-50 text-red-600';
  if (['not_configured', 'disabled'].includes(status)) return 'bg-amber-50 text-amber-600';
  return 'bg-zinc-100 text-zinc-600';
};

export default function CommunicationsPage() {
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [dbas, setDbas] = useState([]);
  const [automationDbaId, setAutomationDbaId] = useState('');
  const [loading, setLoading] = useState(true);
  const [editTemplate, setEditTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewVariables, setPreviewVariables] = useState({});
  const [previewPhone, setPreviewPhone] = useState('');
  const [previewSending, setPreviewSending] = useState(false);
  const [showNewKey, setShowNewKey] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [error, setError] = useState('');
  const [automationResult, setAutomationResult] = useState(null);
  const [templateBody, setTemplateBody] = useState('');
  const [templateChannel, setTemplateChannel] = useState('sms');
  const [emailEditorMode, setEmailEditorMode] = useState('rich');
  const [lastMessageRefreshAt, setLastMessageRefreshAt] = useState(null);
  const [smsCopySettings, setSmsCopySettings] = useState({ enabled: false, copy_to_phone: '', normalized_copy_to_phone: '', configured: false });
  const [smsCopyForm, setSmsCopyForm] = useState({ enabled: false, copy_to_phone: '' });
  const [smsCopySaving, setSmsCopySaving] = useState(false);
  const [smsCopyMessage, setSmsCopyMessage] = useState('');
  const [failureAlerts, setFailureAlerts] = useState([]);
  const [twilioSyncing, setTwilioSyncing] = useState(false);
  const [twilioSyncMessage, setTwilioSyncMessage] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [t, m, k, c, d, smsCopy, failures] = await Promise.all([
        api.get('/templates'),
        api.get('/messages', { params: { limit: 100 } }),
        api.get('/service-keys'),
        api.get('/campaigns'),
        api.get('/dbas'),
        api.get('/communications/sms-copy-settings'),
        api.get('/messages/failure-alerts'),
      ]);
      setTemplates(t.data);
      setMessages(m.data);
      setLastMessageRefreshAt(new Date().toISOString());
      setApiKeys(k.data);
      setCampaigns(c.data);
      setDbas(d.data);
      setSmsCopySettings(smsCopy.data);
      setSmsCopyForm({ enabled: !!smsCopy.data.enabled, copy_to_phone: smsCopy.data.copy_to_phone || smsCopy.data.normalized_copy_to_phone || '' });
      setFailureAlerts(failures.data || []);
    } catch { return; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveTemplate = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const payload = {
      event_type: f.get('event_type'),
      name: f.get('name'),
      channel: f.get('channel'),
      subject: f.get('subject') || '',
      body: templateBody,
      dba_id: f.get('dba_id') || '',
      is_active: true,
    };
    try {
      if (editTemplate?.id) {
        await api.put(`/templates/${editTemplate.id}`, payload);
      } else {
        await api.post('/templates', payload);
      }
      setEditTemplate(null);
      fetchAll();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await api.delete(`/templates/${id}`);
      await fetchAll();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const openTemplateEditor = (template = {}) => {
    setEditTemplate(template);
    setTemplateBody(template.body || '');
    setTemplateChannel(template.channel || 'sms');
    setEmailEditorMode('rich');
    setError('');
  };

  const addEmoji = (emoji) => setTemplateBody(prev => `${prev}${emoji}`);
  const insertSnippet = (snippet) => setTemplateBody(prev => `${prev}${prev && !prev.endsWith('\n') ? '\n' : ''}${snippet}`);
  const applyEmailStarter = () => setTemplateBody(EMAIL_STARTER_HTML);

  const smsSegments = (() => {
    const unicode = Array.from(templateBody).some((character) => character.charCodeAt(0) > 127);
    const limit = unicode ? 70 : 160;
    return { unicode, limit, count: Math.max(1, Math.ceil((templateBody.length || 0) / limit)) };
  })();
  const emailPreviewHtml = templateBody
    .replaceAll('{{brand_color}}', '#18181b')
    .replaceAll('{{business_name}}', 'Butler County Junk Removal')
    .replaceAll('{{business_phone}}', '+1 (513) 916-3638')
    .replaceAll('{{customer_name}}', 'Alex Customer')
    .replaceAll('{{service_category}}', 'Junk Removal')
    .replaceAll('{{quote_link}}', 'https://example.com/quote/qte-1007')
    .replaceAll('{{payment_link}}', 'https://example.com/pay/inv-1007')
    .replaceAll('{{review_link}}', 'https://example.com/review')
    .replaceAll('{{proof_packet_link}}', 'https://example.com/proof/sample');

  const createApiKey = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      const { data } = await api.post('/service-keys', { name: f.get('name') });
      setNewKeyResult(data);
      fetchAll();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Revoke this API key?')) return;
    await api.delete(`/service-keys/${id}`);
    fetchAll();
  };

  const runAutomations = async () => {
    try {
      const params = automationDbaId ? { dba_id: automationDbaId } : {};
      const { data } = await api.get('/automations/check', { params });
      setAutomationResult(data);
    } catch { return; }
  };

  const saveSmsCopySettings = async (event) => {
    event.preventDefault();
    setSmsCopySaving(true);
    setSmsCopyMessage('');
    setError('');
    try {
      const { data } = await api.put('/communications/sms-copy-settings', smsCopyForm);
      setSmsCopySettings(data);
      setSmsCopyForm({ enabled: !!data.enabled, copy_to_phone: data.copy_to_phone || data.normalized_copy_to_phone || '' });
      setSmsCopyMessage(data.enabled ? `SMS copy enabled for ${data.normalized_copy_to_phone || data.copy_to_phone}` : 'SMS copy disabled.');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSmsCopySaving(false);
    }
  };

  const createSmsFailureTask = async (messageId) => {
    try {
      await api.post(`/messages/${messageId}/follow-up-task`);
      await fetchAll();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const resolveSmsFailureTask = async (taskId) => {
    try {
      await api.put(`/message-follow-up-tasks/${taskId}`, { status: 'resolved', resolution_note: 'Resolved from Communications alert queue' });
      await fetchAll();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const syncTwilioStatus = async (messageId) => {
    setTwilioSyncing(true);
    setTwilioSyncMessage('');
    setError('');
    try {
      const { data } = await api.post(`/messages/${messageId}/sync-twilio-status`);
      setTwilioSyncMessage(`Verified with Twilio: ${data.delivery_status || data.status || 'updated'}`);
      await fetchAll();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setTwilioSyncing(false);
    }
  };

  const syncWaitingTwilioStatuses = async () => {
    setTwilioSyncing(true);
    setTwilioSyncMessage('');
    setError('');
    try {
      const { data } = await api.post('/messages/sync-twilio-statuses');
      setTwilioSyncMessage(`Verified ${data.checked || 0} waiting SMS with Twilio. Updated ${data.updated || 0}.`);
      await fetchAll();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setTwilioSyncing(false);
    }
  };

  const dbaName = (id) => dbas.find(d => d.id === id)?.name || 'Global';

  const openPreview = async (template) => {
    setPreviewTemplate(template);
    setPreviewResult(null);
    setPreviewVariables({});
    setPreviewPhone('');
    setError('');
    try {
      const { data } = await api.post(`/templates/${template.id}/preview`, { variables: {} });
      setPreviewResult(data);
      setPreviewVariables(data.variables || {});
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const refreshTemplatePreview = async () => {
    if (!previewTemplate?.id) return;
    setError('');
    try {
      const { data } = await api.post(`/templates/${previewTemplate.id}/preview`, { variables: previewVariables });
      setPreviewResult(data);
      setPreviewVariables(data.variables || {});
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const sendTemplateTest = async () => {
    if (!previewTemplate?.id || !previewPhone.trim()) return;
    setPreviewSending(true);
    setError('');
    try {
      const isEmail = (previewResult?.channel || previewTemplate?.channel) === 'email';
      const { data } = await api.post(`/templates/${previewTemplate.id}/preview`, { send_test: true, [isEmail ? 'test_email' : 'test_phone']: previewPhone, variables: previewVariables });
      setPreviewResult(data);
      setPreviewVariables(data.variables || {});
      await fetchAll();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setPreviewSending(false);
    }
  };


  const createCampaign = async (campaignType) => {
    const tmpl = templates.find(t => t.event_type === 'lead.followup') || templates[0];
    await api.post('/campaigns', { name: `${campaignType.label} Campaign`, type: campaignType.type, template_id: tmpl?.id || '' });
    fetchAll();
  };

  const executeCampaign = async (campaignId) => {
    await api.post(`/campaigns/${campaignId}/execute`);
    fetchAll();
  };

  const openNewKeyDialog = () => {
    setShowNewKey(true);
    setNewKeyResult(null);
    setError('');
  };

  if (loading) return <div className="space-y-4 animate-pulse"><div className="h-36 rounded-[1.75rem] bg-blue-100" /><div className="h-64 rounded-[1.5rem] bg-blue-100" /></div>;

  return (
    <div className="-mx-3 space-y-6 bg-[#eef7ff] px-0 pb-8 pt-0 animate-fade-in sm:mx-0 sm:bg-transparent sm:px-0 sm:pt-0">
      <HighVisibilityPageHeader
        eyebrow="Customer messaging"
        title="Communications"
        subtitle="Templates, delivery status, workflows, campaigns, and API access in one place."
        countLabel={`${messages.length} messages · ${templates.length} templates`}
        icon={MessageSquareText}
        tone="blue"
        testId="comms-high-visibility-header"
        titleTestId="comms-title"
      />

      <div className="px-3 sm:px-0">
      <CommunicationsTabs
        data={{ templates, messages, campaigns, apiKeys, dbas, automationDbaId, automationResult, lastMessageRefreshAt, smsCopySettings, smsCopyForm, smsCopySaving, smsCopyMessage, failureAlerts, twilioSyncing, twilioSyncMessage }}
        actions={{ openTemplateEditor, deleteTemplate, openPreview, setAutomationDbaId, runAutomations, createCampaign, executeCampaign, revokeKey, openNewKeyDialog, refreshMessages: fetchAll, setSmsCopyForm, saveSmsCopySettings, createSmsFailureTask, resolveSmsFailureTask, syncTwilioStatus, syncWaitingTwilioStatuses }}
        helpers={{ dbaName, messageStatusClass }}
      />
      </div>

      <CommunicationsDialogs
        data={{ editTemplate, templateChannel, templateBody, emailEditorMode, error, smsSegments, emailPreviewHtml, previewTemplate, previewResult, previewVariables, previewPhone, previewSending, showNewKey, newKeyResult }}
        actions={{ setEditTemplate, setTemplateBody, setTemplateChannel, setEmailEditorMode, saveTemplate, addEmoji, applyEmailStarter, insertSnippet, setPreviewTemplate, setPreviewResult, setPreviewVariables, setPreviewPhone, refreshTemplatePreview, sendTemplateTest, setShowNewKey, createApiKey }}
        helpers={{ dbaName, dbas }}
      />
    </div>
  );
}
